import type { RuntimeConfig } from "@/config/schema";

import { createDevStubAdapter } from "./adapters/devStubAdapter";
import type { AuthAdapter } from "./types";

/**
 * Selects the identity implementation from runtime configuration.
 *
 * The schema has already rejected any configuration that would allow a stub
 * identity outside local development, so this function does not need to
 * re-litigate the decision — it only has to avoid inventing a default. There is
 * no fallback branch: an unrecognised mode is a type error, not an anonymous
 * session.
 *
 * The MSAL adapter is imported dynamically so that `@azure/msal-browser`
 * lands in its own chunk and is never downloaded by a local development
 * session. The configuration mode stays named "oidc": it describes the
 * protocol the environment speaks, not the library that implements it.
 */
export async function createAuthAdapter(
  config: RuntimeConfig,
): Promise<AuthAdapter> {
  switch (config.auth.mode) {
    case "oidc": {
      const { createMsalAdapter } = await import("./adapters/msalAdapter");
      return createMsalAdapter(config);
    }
    case "dev-stub":
      return createDevStubAdapter();
  }
}
